/**
 * Catálogo único das telas do painel.
 *
 * Existia uma lista de menu por papel (admin, vendedor, balcão) e uma lista de
 * rotas por papel no App.js — seis listas para o mesmo conjunto de telas. Era a
 * causa daquele problema: o cadastro de perfis liberava "Financeiro" para um
 * vendedor e nada acontecia, porque o menu e as rotas do painel do vendedor não
 * conheciam essa tela. O perfil prometia, o painel não entregava.
 *
 * Agora a fonte é uma só. Cada painel monta menu e rotas a partir daqui, e quem
 * decide o que aparece é a permissão do perfil — a mesma chave que o servidor
 * confere em `permission:`.
 *
 * Para acrescentar uma tela: uma entrada aqui, uma chave em
 * `App\Support\Permissions::GROUPS` e o componente em `SCREEN_ELEMENTS`
 * (App.js). Os três painéis ganham a tela juntos.
 *
 * `kw` são as abreviações que a equipe digita na paleta (⌘K) e que NÃO saem do
 * rótulo — "OS" não é substring de "Ordens de Serviço", "NF" não é de "Fiscal".
 */
import { useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, ClipboardList, ScanBarcode, Percent, ArrowLeftRight, FileText,
  Wallet, Users, UserCog, Ticket, KeyRound, ShoppingCart, Star,
  BarChart3, Settings, Contact, Tags, Truck, Inbox, FileUp, Wrench, CalendarDays,
  Receipt, UserPlus, ShieldCheck, Images,
} from "lucide-react";

/**
 * `path` é o trecho depois do painel: no admin vira `/admin/<path>`, no
 * vendedor `/vendedor/<path>`. Path vazio marca a tela inicial daquele painel.
 *
 * A ordem das seções e dos itens é a ordem do menu, e espelha
 * `Permissions::GROUPS` no servidor.
 */
export const SCREENS = [
  { section: "Visão Geral", perm: "dashboard", path: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { section: "Visão Geral", perm: "relatorios", path: "relatorios", label: "Relatórios", icon: BarChart3, kw: "relatorio grafico" },

  { section: "Operação", perm: "pedidos", path: "pedidos", label: "Pedidos", icon: ClipboardList },
  { section: "Operação", perm: "produtos", path: "produtos", label: "Produtos", icon: Package },
  { section: "Operação", perm: "categorias", path: "categorias", label: "Categorias", icon: Tags },
  { section: "Operação", perm: "fornecedores", path: "fornecedores", label: "Fornecedores", icon: Truck },
  { section: "Operação", perm: "estoque", path: "estoque", label: "Estoque", icon: ArrowLeftRight },
  { section: "Operação", perm: "importar-xml", path: "importar-xml", label: "Importar XML", icon: FileUp, kw: "xml nfe entrada nota" },
  // Mesma chave, papéis opostos: o admin APROVA solicitações, quem está no
  // balcão ABRE. São telas diferentes (ver SCREEN_ELEMENTS) e o rótulo segue o
  // que a pessoa faz ali.
  {
    section: "Operação", perm: "solicitacoes", path: "solicitacoes", label: "Solicitações", icon: Inbox,
    labelByRole: { vendedor: "Solicitar Alteração", balcao: "Solicitar Alteração" },
  },
  { section: "Operação", perm: "ordens-servico", path: "ordens-servico", label: "Ordens de Serviço", icon: Wrench, kw: "os ordem servico conserto" },
  { section: "Operação", perm: "agenda", path: "agenda", label: "Agenda", icon: CalendarDays },
  // `orcamentos` NÃO é mais item de menu: os orçamentos passaram a viver na
  // mesma lista das vendas (tela "Vendas & Orçamentos", seção Balcão). A chave
  // continua existindo como PERMISSÃO — é ela que libera "Gerar orçamento" no
  // PDV e o acesso à lista unificada. Ver Permissions::ACTIONS no servidor.
  { section: "Operação", perm: "alugueis", path: "alugueis", label: "Aluguéis", icon: KeyRound },
  { section: "Operação", perm: "carrinhos", path: "carrinhos", label: "Carrinhos Aband.", icon: ShoppingCart, kw: "abandonados carrinho recuperacao" },

  { section: "Balcão", perm: "pdv", path: "pdv", label: "PDV — Venda Balcão", icon: ScanBarcode, kw: "pdv caixa venda balcao" },
  {
    section: "Balcão", perm: "vendas", path: "vendas", label: "Vendas & Orçamentos", icon: Receipt,
    kw: "venda orcamento proposta aberta pendura balcao",
  },
  { section: "Balcão", perm: "frente-caixa", path: "frente-caixa", label: "Frente de Caixa", icon: Wallet, kw: "caixa sangria suprimento fechamento abertura" },
  { section: "Balcão", perm: "comissoes", path: "comissoes", label: "Comissões", icon: Percent, kw: "comissao" },
  { section: "Balcão", perm: "clientes-atendidos", path: "clientes-atendidos", label: "Clientes Atendidos", icon: Contact },

  { section: "Financeiro", perm: "financeiro", path: "financeiro", label: "Financeiro", icon: Wallet },
  { section: "Financeiro", perm: "fiscal", path: "fiscal", label: "Fiscal", icon: FileText, kw: "nf nfe nfce nota danfe sefaz" },
  { section: "Financeiro", perm: "cupons", path: "cupons", label: "Cupons", icon: Ticket },

  { section: "Loja", perm: "banners", path: "banners", label: "Banners da Home", icon: Images, kw: "banner slider carrossel campanha promocao anuncio vitrine" },

  { section: "Relacionamento", perm: "crm", path: "crm", label: "Clientes", icon: Users, kw: "crm cliente cadastro" },
  { section: "Relacionamento", perm: "vendedores", path: "vendedores", label: "Vendedores", icon: UserCog, kw: "vendedor balcao equipe" },
  { section: "Relacionamento", perm: "avaliacoes", path: "avaliacoes", label: "Avaliações", icon: Star },

  { section: "Sistema", perm: "usuarios", path: "usuarios", label: "Usuários", icon: UserPlus },
  { section: "Sistema", perm: "perfis", path: "perfis", label: "Perfis de Acesso", icon: ShieldCheck },
  { section: "Sistema", perm: "configuracoes", path: "configuracoes", label: "Configurações", icon: Settings, kw: "config ajustes" },
];

/** Caminho do painel de cada papel. */
export const PANEL_BASE = { admin: "/admin", vendedor: "/vendedor", balcao: "/balcao" };

/**
 * Tela inicial de cada painel — a que responde na raiz.
 *
 * O admin abre no Dashboard; balcão e vendedor abrem no PDV, que é onde a
 * pessoa passa o dia. Sem permissão para ela, a raiz mostra o aviso de acesso
 * (Protected/NoAccess), e não uma tela em branco.
 */
export const PANEL_HOME = { admin: "dashboard", vendedor: "pdv", balcao: "pdv" };

/**
 * Endereços antigos dos painéis de vendedor e balcão, antes de os caminhos
 * seguirem a chave da permissão. Ficam como redirecionamento para não quebrar
 * link salvo nem atalho na tela inicial do tablet do balcão.
 *
 * `/vendedor/estoque` NÃO entra aqui de propósito: antes significava Consulta
 * de Estoque, e agora é a tela de Estoque, que passou a existir nesse painel.
 * Um redirecionamento nunca dispararia (a rota real casa primeiro) e, se
 * disparasse, levaria a pessoa para longe do endereço que ela pediu. Entre
 * honrar o link antigo e honrar o endereço atual, vale o atual.
 */
export const LEGACY_REDIRECTS = {
  // `orcamentos` e `consulta-estoque` deixaram de ser telas: a primeira virou
  // parte de "Vendas & Orçamentos", a segunda foi absorvida por Estoque (que
  // responde a mesma pergunta com mais informação). Os endereços continuam
  // válidos — há link salvo e atalho na tela inicial do tablet do balcão.
  admin: { orcamentos: "vendas", "consulta-estoque": "estoque" },
  vendedor: { clientes: "clientes-atendidos", orcamentos: "vendas", "consulta-estoque": "estoque" },
  balcao: { clientes: "clientes-atendidos", orcamentos: "vendas", "consulta-estoque": "estoque" },
};

/** Caminho completo de uma tela dentro do painel do papel. */
export const screenPath = (role, screen) =>
  (screen.perm === PANEL_HOME[role] ? PANEL_BASE[role] : `${PANEL_BASE[role]}/${screen.path}`);

/** Em qual painel a pessoa está agora, pela URL. */
export function usePanelRole() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/vendedor")) return "vendedor";
  if (pathname.startsWith("/balcao")) return "balcao";

  return "admin";
}

/**
 * Link para outra tela DO PAINEL ATUAL.
 *
 * As telas apontavam umas para as outras com `/admin/...` na mão. Como o mesmo
 * componente agora serve os três painéis, um vendedor no Dashboard clicando em
 * "Ver todos" ia para `/admin/pedidos`, batia no `roles={["admin"]}` e era
 * despejado na loja. Aqui o caminho acompanha o painel de quem está olhando.
 */
export function usePanelLink() {
  const role = usePanelRole();

  return (perm) => {
    const screen = SCREENS.find((s) => s.perm === perm);

    return screen ? screenPath(role, screen) : PANEL_BASE[role];
  };
}
